import { cn } from "@/lib/utils";
import { useCallback, useRef, useState } from "react";

interface MagicMomentFillInProps {
  placeholder: string;
  onChange: (value: string) => void;
}

export function MagicMomentFillIn({
  placeholder,
  onChange,
}: MagicMomentFillInProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [filled, setFilled] = useState(false);

  // Treat the bracketed placeholder as the "empty" state. If the mentor's
  // current text equals the bracketed placeholder string, it's still empty.
  const handleInput = useCallback(() => {
    const text = ref.current?.textContent ?? "";
    const isPlaceholder = text === `[${placeholder}]`;
    setFilled(!isPlaceholder && text.length > 0);
    onChange(isPlaceholder ? "" : text);
  }, [placeholder, onChange]);

  const handleFocus = () => {
    const node = ref.current;
    if (!node) return;
    if ((node.textContent ?? "") === `[${placeholder}]`) {
      node.textContent = "";
      setFilled(false);
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleBlur = () => {
    const text = ref.current?.textContent ?? "";
    if (text.trim().length === 0) {
      if (ref.current) ref.current.textContent = `[${placeholder}]`;
      setFilled(false);
      onChange("");
    }
  };

  return (
    <span
      ref={ref}
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn(
        "inline-block min-w-[1ch] border-b-2 px-0.5 pb-0.5 outline-none focus:border-text-primary",
        filled
          ? "border-text-primary text-text-primary"
          : "border-surfaces-z4 text-text-tertiary",
      )}
    >
      {`[${placeholder}]`}
    </span>
  );
}
